import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  Divider,
  Avatar,
  IconButton,
  ListSubheader,
  Paper,
  Collapse,
  alpha,
  Fade,
} from "@mui/material";
import { useTheme, styled } from "@mui/material/styles";

import { Link, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

// Enhanced styled components
const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  position: "relative",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    top: "50%",
    transform: "translateY(-50%) scaleY(0)",
    width: 3,
    height: "60%",
    background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    borderRadius: "0 2px 2px 0",
    transition: "transform 0.3s ease",
  },
  "&:hover": {
    transform: "translateX(4px)",
    "&::after": {
      transform: "translateY(-50%) scaleY(1)",
    },
  },
  "&.Mui-selected": {
    "&::after": {
      transform: "translateY(-50%) scaleY(1)",
    },
  },
}));

const StyledListSubheader = styled(ListSubheader)(({ theme }) => ({
  background: "transparent",
  fontWeight: 700,
  fontSize: "0.7rem",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  color: theme.palette.text.secondary,
  opacity: 0.8,
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: "50%",
    width: 20,
    height: 1,
    background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.5)}, transparent)`,
  },
}));

import DashboardIcon from "@mui/icons-material/Dashboard";
import HomeIcon from "@mui/icons-material/Home";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import InfoIcon from "@mui/icons-material/Info";
import SettingsIcon from "@mui/icons-material/Settings";
import PeopleIcon from "@mui/icons-material/People";
import ApartmentIcon from "@mui/icons-material/Apartment";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import CloseIcon from "@mui/icons-material/Close";
import BusinessIcon from "@mui/icons-material/Business";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// HR block icons
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import FlightTakeoffOutlinedIcon from "@mui/icons-material/FlightTakeoffOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import BalanceOutlinedIcon from "@mui/icons-material/BalanceOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// CRM block icons
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";

// Inventory, Purchasing, Invoicing icons
import InventoryIcon from "@mui/icons-material/Inventory";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ReceiptIcon from "@mui/icons-material/Receipt";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StoreIcon from "@mui/icons-material/Store";
import PaymentIcon from "@mui/icons-material/Payment";

import noveraLogo from "../../assets/Novera.png";
import { getUserInfoFromToken } from "../../utils/Main";
import axios from "axios";
import { baseUrl } from "../../constants";

type IconMappingKeys =
  | "Dashboard"
  | "Home"
  | "Profile"
  | "Projects"
  | "Departments"
  | "Branches"
  | "Account"
  | "Approvals"
  | "AssignmentSettings"
  | "Workspace"
  | "Users"
  | "NoteBook"
  | "Settings"
  | "About"
  | "HR"
  | "CRM"
  | "Inventory"
  | "Purchasing"
  | "Invoicing";

interface SideBarProps {
  isMobile: boolean;
  drawerWidth: number;
  mobileOpen: boolean;
  sidebarOpen: boolean;
  handleDrawerToggle: () => void;
  handleSidebarToggle: () => void;
  sx?: object;
}

const HR_LOCAL_KEY = "nav:hr:open";
const CRM_LOCAL_KEY = "nav:crm:open";
const INVENTORY_LOCAL_KEY = "nav:inventory:open";
const PURCHASING_LOCAL_KEY = "nav:purchasing:open";
const INVOICING_LOCAL_KEY = "nav:invoicing:open";

const SideBar = ({
  isMobile,
  drawerWidth,
  mobileOpen,
  sidebarOpen,
  handleDrawerToggle,
  handleSidebarToggle,
  sx,
}: SideBarProps) => {
  const theme = useTheme();
  const location = useLocation();

  const userInfo = getUserInfoFromToken();
  const role = userInfo?.role ?? "user";
  const fullName = userInfo?.fullName ?? userInfo?.username ?? "User";
  const workspaceName = userInfo?.workspaceName ?? "Workspace";

  const [sideBarOptions, setSideBarOptions] = useState<IconMappingKeys[]>([]);
  const [hrOpen, setHrOpen] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(HR_LOCAL_KEY);
      return raw === "1";
    } catch {
      return false;
    }
  });
  const [crmOpen, setCrmOpen] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(CRM_LOCAL_KEY);
      return raw === "1";
    } catch {
      return false;
    }
  });

  const [inventoryOpen, setInventoryOpen] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(INVENTORY_LOCAL_KEY);
      return raw === "1";
    } catch {
      return false;
    }
  });

  const [purchasingOpen, setPurchasingOpen] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(PURCHASING_LOCAL_KEY);
      return raw === "1";
    } catch {
      return false;
    }
  });

  const [invoicingOpen, setInvoicingOpen] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(INVOICING_LOCAL_KEY);
      return raw === "1";
    } catch {
      return false;
    }
  });

  // ⬇️ keep avatar URL from /users/:id/assignments (same as Profile)
  const [avatarUrl, setAvatarUrl] = useState<string>("");

  // helper to make relative URLs absolute
  const normalizeImageUrl = (url?: string) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${baseUrl}/${url}`;
  };

  useEffect(() => {
    const fetchAvatar = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const userId = getUserInfoFromToken()?.id;
        if (!token || !userId) return;

        const res = await axios.get(`${baseUrl}/users/${userId}/assignments`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const raw = res.data || {};
        const possibleImg =
          raw?.profile?.imageUrl ??
          raw?.imageUrl ??
          raw?.profileImage ??
          raw?.avatar;

        if (possibleImg) {
        setAvatarUrl(normalizeImageUrl(possibleImg));
        }
      } catch (error) {
        console.error("Failed to fetch avatar:", error);
      }
    };

    fetchAvatar();
  }, []);

  useEffect(() => {
    // Base options available to all users
    const baseOptions: IconMappingKeys[] = ["Dashboard", "Home", "Profile", "Projects", "Departments", "Account", "Users", "NoteBook", "Settings", "About", "HR", "CRM", "Inventory", "Purchasing", "Invoicing"];
    
    // Admin-only options
    const adminOptions: IconMappingKeys[] = ["Branches", "Approvals", "AssignmentSettings", "Workspace"];
    
    // Combine base options with admin options if user is admin
    const options = role === "admin" ? [...baseOptions, ...adminOptions] : baseOptions;

    setSideBarOptions(options);
  }, [role]);

  const iconColor =
    theme.palette.mode === "dark" ? theme.palette.text.secondary : "#48545f";

  const getIconComponent = (text: IconMappingKeys) => {
    switch (text) {
      case "Dashboard":
        return <DashboardIcon sx={{ color: iconColor }} />;
      case "Home":
        return <HomeIcon sx={{ color: iconColor }} />;
      case "Profile":
        return <AccountCircleIcon sx={{ color: iconColor }} />;
      case "Projects":
        return <BusinessIcon sx={{ color: iconColor }} />;
      case "Departments":
        return <CorporateFareIcon sx={{ color: iconColor }} />;
      case "Branches":
        return <ApartmentIcon sx={{ color: iconColor }} />;
      case "Account":
        return <AccountCircleIcon sx={{ color: iconColor }} />;
      case "Approvals":
        return <AssignmentIcon sx={{ color: iconColor }} />;
      case "AssignmentSettings":
        return <AssignmentIcon sx={{ color: iconColor }} />;
      case "Workspace":
        return <BusinessIcon sx={{ color: iconColor }} />;
      case "Users":
        return <PeopleIcon sx={{ color: iconColor }} />;
      case "NoteBook":
        return <MenuBookOutlinedIcon sx={{ color: iconColor }} />;
      case "Settings":
        return <SettingsIcon sx={{ color: iconColor }} />;
      case "About":
        return <InfoIcon sx={{ color: iconColor }} />;
      case "HR":
        return <BusinessCenterOutlinedIcon sx={{ color: iconColor }} />;
      case "CRM":
        return <AccountTreeOutlinedIcon sx={{ color: iconColor }} />;
      case "Inventory":
        return <InventoryIcon sx={{ color: iconColor }} />;
      case "Purchasing":
        return <ShoppingCartIcon sx={{ color: iconColor }} />;
      case "Invoicing":
        return <ReceiptIcon sx={{ color: iconColor }} />;
      default:
        return <DashboardIcon sx={{ color: iconColor }} />;
    }
  };

  const pathMap: Record<Exclude<IconMappingKeys, "HR" | "CRM" | "Inventory" | "Purchasing" | "Invoicing">, string> = {
    Dashboard: "/dashboard",
    Home: "/home",
    Profile: "/profile",
    Projects: "/projects",
    Departments: "/departments",
    Branches: "/branches",
    Account: "/account",
    Approvals: "/approvals",
    AssignmentSettings: "/assignment-settings",
    Workspace: "/workspace",
    Users: "/users",
    NoteBook: "/notebook",
    Settings: "/settings",
    About: "/about",
  };

  // ---------- HR nested items ----------
  const hrChildren = useMemo(
    () => [
      { label: "Dashboard", to: "/hr", icon: <DashboardOutlinedIcon fontSize="small" /> },
      { label: "Leaves", to: "/hr/leaves", icon: <EventAvailableOutlinedIcon fontSize="small" /> },
      { label: "Travels", to: "/hr/travels", icon: <FlightTakeoffOutlinedIcon fontSize="small" /> },
      { label: "Advances", to: "/hr/expense-advances", icon: <PaidOutlinedIcon fontSize="small" /> },
      { label: "Loans", to: "/hr/loans", icon: <MonetizationOnOutlinedIcon fontSize="small" /> },
      ...(role === "admin"
        ? [
            { label: "Balances", to: "/hr/admin/leave-balances", icon: <BalanceOutlinedIcon fontSize="small" /> },
            { label: "Holidays", to: "/hr/admin/holiday-calendars", icon: <CalendarMonthOutlinedIcon fontSize="small" /> },
          ]
        : []),
      { label: "Reports", to: "/hr/reports", icon: <AssessmentOutlinedIcon fontSize="small" /> },
    ],
    [role]
  );

  const crmChildren = useMemo(
    () => [
      { label: "Dashboard", to: "/crm", icon: <DashboardOutlinedIcon fontSize="small" /> },
      { label: "Leads", to: "/crm/leads", icon: <PeopleAltOutlinedIcon fontSize="small" /> },
      { label: "Contacts", to: "/crm/contacts", icon: <AccountTreeOutlinedIcon fontSize="small" /> },
      { label: "Opportunities", to: "/crm/opportunities", icon: <TrendingUpOutlinedIcon fontSize="small" /> },
      { label: "Activities", to: "/crm/activities", icon: <PhoneOutlinedIcon fontSize="small" /> },
    ],
    []
  );

  const inventoryChildren = useMemo(
    () => [
      { label: "Products", to: "/inventory/products", icon: <InventoryIcon fontSize="small" /> },
      { label: "Warehouses", to: "/inventory/warehouses", icon: <WarehouseIcon fontSize="small" /> },
      { label: "Stock Levels", to: "/inventory/stock-levels", icon: <AssessmentOutlinedIcon fontSize="small" /> },
      { label: "Stock Movements", to: "/inventory/stock-movements", icon: <LocalShippingIcon fontSize="small" /> },
    ],
    []
  );

  const purchasingChildren = useMemo(
    () => [
      { label: "Suppliers", to: "/purchasing/suppliers", icon: <StoreIcon fontSize="small" /> },
      { label: "Purchase Orders", to: "/purchasing/purchase-orders", icon: <ShoppingCartIcon fontSize="small" /> },
      { label: "Goods Receipts", to: "/purchasing/goods-receipts", icon: <LocalShippingIcon fontSize="small" /> },
    ],
    []
  );

  const invoicingChildren = useMemo(
    () => [
      { label: "Invoices", to: "/invoicing/invoices", icon: <ReceiptIcon fontSize="small" /> },
      { label: "Payments", to: "/invoicing/payments", icon: <PaymentIcon fontSize="small" /> },
      { label: "Credit Notes", to: "/invoicing/credit-notes", icon: <ReceiptIcon fontSize="small" /> },
    ],
    []
  );

  // Helper: is current route under /hr/**
  const isHRActive = location.pathname.startsWith("/hr");
  // Helper: is current route under /crm/**
  const isCRMActive = location.pathname.startsWith("/crm");
  // Helper: is current route under /inventory/**
  const isInventoryActive = location.pathname.startsWith("/inventory");
  // Helper: is current route under /purchasing/**
  const isPurchasingActive = location.pathname.startsWith("/purchasing");
  // Helper: is current route under /invoicing/**
  const isInvoicingActive = location.pathname.startsWith("/invoicing");

  // Auto-open HR if on /hr/**; otherwise respect persisted user choice (and mobile default collapsed)
  useEffect(() => {
    if (isHRActive && !hrOpen) {
      setHrOpen(true);
      try {
        localStorage.setItem(HR_LOCAL_KEY, "1");
      } catch {
        // Silent fail
      }
    }
  }, [isHRActive, hrOpen]);

  // Auto-open CRM if on /crm/**; otherwise respect persisted user choice (and mobile default collapsed)
  useEffect(() => {
    if (isCRMActive && !crmOpen) {
      setCrmOpen(true);
      try {
        localStorage.setItem(CRM_LOCAL_KEY, "1");
      } catch {
        // Silent fail
      }
    }
  }, [isCRMActive, crmOpen]);

  // Auto-open Inventory if on /inventory/**
  useEffect(() => {
    if (isInventoryActive && !inventoryOpen) {
      setInventoryOpen(true);
      try {
        localStorage.setItem(INVENTORY_LOCAL_KEY, "1");
      } catch {
        // Silent fail
      }
    }
  }, [isInventoryActive, inventoryOpen]);

  // Auto-open Purchasing if on /purchasing/**
  useEffect(() => {
    if (isPurchasingActive && !purchasingOpen) {
      setPurchasingOpen(true);
      try {
        localStorage.setItem(PURCHASING_LOCAL_KEY, "1");
      } catch {
        // Silent fail
      }
    }
  }, [isPurchasingActive, purchasingOpen]);

  // Auto-open Invoicing if on /invoicing/**
  useEffect(() => {
    if (isInvoicingActive && !invoicingOpen) {
      setInvoicingOpen(true);
      try {
        localStorage.setItem(INVOICING_LOCAL_KEY, "1");
      } catch {
        // Silent fail
      }
    }
  }, [isInvoicingActive, invoicingOpen]);

  // Persist manual toggle
  const toggleHR = () => {
    setHrOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(HR_LOCAL_KEY, next ? "1" : "0");
      } catch {
        // Ignore localStorage errors
      }
      return next;
    });
  };

  // CRM toggle function
  const toggleCRM = () => {
    setCrmOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(CRM_LOCAL_KEY, next ? "1" : "0");
      } catch {
        // Ignore localStorage errors
      }
      return next;
    });
  };

  // Inventory toggle function
  const toggleInventory = () => {
    setInventoryOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(INVENTORY_LOCAL_KEY, next ? "1" : "0");
      } catch {
        // Ignore localStorage errors
      }
      return next;
    });
  };

  // Purchasing toggle function
  const togglePurchasing = () => {
    setPurchasingOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(PURCHASING_LOCAL_KEY, next ? "1" : "0");
      } catch {
        // Ignore localStorage errors
      }
      return next;
    });
  };

  // Invoicing toggle function
  const toggleInvoicing = () => {
    setInvoicingOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(INVOICING_LOCAL_KEY, next ? "1" : "0");
      } catch {
        // Ignore localStorage errors
      }
      return next;
    });
  };

  const getNestedItemStyles = (active: boolean) => ({
    my: 0.5,
    borderRadius: 2.5,
    position: "relative",
    minHeight: 36,
    ml: 2,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      bgcolor: alpha(theme.palette.primary.main, 0.08),
      transform: "translateX(4px)",
      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
    },
    ...(active && {
      bgcolor: alpha(theme.palette.primary.main, 0.12),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: 3,
        background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        borderRadius: "0 2px 2px 0",
        boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.4)}`,
      },
      "&:hover": {
        bgcolor: alpha(theme.palette.primary.main, 0.15),
        transform: "translateX(4px)",
      },
    }),
  });

  /** Group items into sections */
  const grouped = useMemo(() => {
    const groups: { title: string; keys: IconMappingKeys[] }[] = [];

    const overview: IconMappingKeys[] = ["Dashboard", "Home", "Profile"];
    const work: IconMappingKeys[] = [
      "Projects",
      "Departments",
      "Account",
      "NoteBook",
    ];
    const adminTools: IconMappingKeys[] = [
      "Branches",
      "Approvals",
      "AssignmentSettings",
      "Workspace",
    ];
    const hr: IconMappingKeys[] = sideBarOptions.includes("HR")
      ? ["HR"]
      : [];
    const crm: IconMappingKeys[] = sideBarOptions.includes("CRM")
      ? ["CRM"]
      : [];
    const inventory: IconMappingKeys[] = sideBarOptions.includes("Inventory")
      ? ["Inventory"]
      : [];
    const purchasing: IconMappingKeys[] = sideBarOptions.includes("Purchasing")
      ? ["Purchasing"]
      : [];
    const invoicing: IconMappingKeys[] = sideBarOptions.includes("Invoicing")
      ? ["Invoicing"]
      : [];
    const people: IconMappingKeys[] = sideBarOptions.includes("Users")
      ? ["Users"]
      : [];
    const system: IconMappingKeys[] = ["Settings", "About"];

    const onlyExisting = (arr: IconMappingKeys[]) =>
      arr.filter((k) => sideBarOptions.includes(k));

    const pushIfAny = (title: string, keys: IconMappingKeys[]) => {
      const list = onlyExisting(keys);
      if (list.length) groups.push({ title, keys: list });
    };

    pushIfAny("Overview", overview);
    pushIfAny("Work", work);
    pushIfAny("Admin Tools", adminTools);
    pushIfAny("HR", hr);
    pushIfAny("CRM", crm);
    pushIfAny("Inventory", inventory);
    pushIfAny("Purchasing", purchasing);
    pushIfAny("Invoicing", invoicing);
    pushIfAny("People", people);
    pushIfAny("System", system);

    return groups;
  }, [sideBarOptions]);

  const initials = (fullName || "U")
    .trim()
    .split(" ")
    .map((n: string) => n.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();

  /** ---------- Ultra-Advanced Header with Enhanced Logo Design ---------- */
  const Header = (
    <Box sx={{ px: 1.5, pt: 1.5, position: "relative", zIndex: 1 }}>
      {/* Ultra-Advanced App Brand - Cutting-Edge Design */}
      <Paper
        elevation={0}
        sx={{
          mb: 1,
          px: 2,
          py: 1.5,
          borderRadius: 5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          background: theme.palette.mode === "dark"
            ? `linear-gradient(135deg, 
                ${alpha(theme.palette.grey[800], 0.95)} 0%, 
                ${alpha(theme.palette.grey[700], 0.98)} 25%,
                ${alpha(theme.palette.grey[600], 0.95)} 50%,
                ${alpha(theme.palette.grey[700], 0.98)} 75%,
                ${alpha(theme.palette.grey[800], 0.95)} 100%)`
            : `linear-gradient(135deg, 
                ${alpha(theme.palette.background.paper, 0.98)} 0%, 
                ${alpha(theme.palette.grey[50], 0.95)} 25%,
                ${alpha(theme.palette.grey[100], 0.98)} 50%,
                ${alpha(theme.palette.grey[50], 0.95)} 75%,
                ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
          border: `3px solid transparent`,
          backgroundImage: `linear-gradient(${theme.palette.mode === "dark" ? alpha(theme.palette.grey[800], 0.95) : alpha(theme.palette.background.paper, 0.98)}, ${theme.palette.mode === "dark" ? alpha(theme.palette.grey[800], 0.95) : alpha(theme.palette.background.paper, 0.98)}), 
                           linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.3)}, ${alpha(theme.palette.secondary.main, 0.2)}, ${alpha(theme.palette.primary.main, 0.3)})`,
          backgroundOrigin: "border-box",
          backgroundClip: "content-box, border-box",
          position: "relative",
          overflow: "hidden",
          transition: "all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          "&:hover": {
            transform: "translateY(-3px) scale(1.02) rotateX(2deg)",
            boxShadow: theme.palette.mode === "dark"
              ? `0 25px 50px ${alpha(theme.palette.common.black, 0.6)}, 
                 0 0 0 2px ${alpha(theme.palette.primary.main, 0.5)},
                 0 0 30px ${alpha(theme.palette.primary.main, 0.2)}`
              : `0 25px 50px ${alpha(theme.palette.common.black, 0.25)}, 
                 0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)},
                 0 0 30px ${alpha(theme.palette.primary.main, 0.15)}`,
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: "-100%",
            left: "-100%",
            width: "300%",
            height: "300%",
            background: `conic-gradient(from 0deg, 
              transparent 0deg,
              ${alpha(theme.palette.primary.main, 0.15)} 60deg,
              ${alpha(theme.palette.secondary.main, 0.12)} 120deg,
              ${alpha(theme.palette.primary.main, 0.1)} 180deg,
              ${alpha(theme.palette.secondary.main, 0.08)} 240deg,
              transparent 300deg,
              transparent 360deg)`,
            animation: "rotate 8s linear infinite",
            opacity: 0.4,
            pointerEvents: "none",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(ellipse at 20% 30%, 
              ${alpha(theme.palette.primary.main, 0.08)} 0%, 
              transparent 40%),
              radial-gradient(ellipse at 80% 70%, 
              ${alpha(theme.palette.secondary.main, 0.06)} 0%, 
              transparent 50%),
              radial-gradient(ellipse at 50% 50%, 
              ${alpha(theme.palette.primary.main, 0.04)} 0%, 
              transparent 60%)`,
            pointerEvents: "none",
            animation: "pulse 4s ease-in-out infinite",
          },
          "@keyframes rotate": {
            "0%": {
              transform: "rotate(0deg)",
            },
            "100%": {
              transform: "rotate(360deg)",
            },
          },
          "@keyframes pulse": {
            "0%, 100%": {
              opacity: 0.3,
            },
            "50%": {
              opacity: 0.7,
            },
          },
        }}
      >
        {/* Simplified Logo Container */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 48,
            height: 48,
            borderRadius: 3,
            background: `linear-gradient(135deg, 
              ${alpha(theme.palette.primary.main, 0.08)} 0%, 
              ${alpha(theme.palette.secondary.main, 0.1)} 50%,
              ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
            border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            overflow: "hidden",
            transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            zIndex: 2,
            flexShrink: 0,
            "&:hover": {
              transform: "scale(1.05) rotate(2deg)",
              boxShadow: `0 10px 25px ${alpha(theme.palette.primary.main, 0.25)}, 
                         0 0 0 1px ${alpha(theme.palette.primary.main, 0.3)}`,
              borderColor: alpha(theme.palette.primary.main, 0.4),
          },
        }}
      >
        <Box
          component="img"
            src={noveraLogo}
            alt="NOVERA ERP SYSTEM"
          sx={{ 
              width: 52, 
              height: 42, 
              borderRadius: 2,
              objectFit: "contain",
              position: "relative",
              zIndex: 2,
              transition: "all 0.3s ease",
              filter: theme.palette.mode === "dark" 
                ? "brightness(1.1) contrast(1.15) saturate(1.05)"
                : "brightness(1.02) contrast(1.05) saturate(1.02)",
            "&:hover": {
                transform: "scale(1.03)",
                filter: theme.palette.mode === "dark" 
                  ? "brightness(1.2) contrast(1.25) saturate(1.1)"
                  : "brightness(1.05) contrast(1.1) saturate(1.05)",
            },
          }}
        />
        </Box>

        {/* Ultra-Advanced Brand Text */}
        <Box sx={{ minWidth: 0, flex: 1, position: "relative", zIndex: 2, overflow: "hidden" }}>
          <Typography
            variant="h6"
            fontWeight={900}
            sx={{ 
              lineHeight: 1.05,
              background: `linear-gradient(135deg, 
                ${theme.palette.text.primary} 0%, 
                ${theme.palette.primary.main} 20%,
                ${theme.palette.secondary.main} 40%,
                ${theme.palette.primary.main} 60%,
                ${theme.palette.secondary.main} 80%,
                ${theme.palette.text.primary} 100%)`,
              backgroundSize: "300% 300%",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontSize: "1.15rem",
              letterSpacing: "-0.03em",
              animation: "textShimmer 4s ease-in-out infinite",
              textShadow: theme.palette.mode === "dark"
                ? `0 0 20px ${alpha(theme.palette.primary.main, 0.3)}, 
                   0 0 40px ${alpha(theme.palette.secondary.main, 0.2)}`
                : "none",
              transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
              position: "relative",
              "&:hover": {
                animation: "textPulse 0.6s ease-in-out",
                transform: "translateX(2px)",
              },
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(135deg, 
                  ${alpha(theme.palette.primary.main, 0.05)} 0%, 
                  ${alpha(theme.palette.secondary.main, 0.03)} 50%,
                  ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                borderRadius: 2,
                animation: "textGlow 3s ease-in-out infinite",
                zIndex: -1,
              },
              "@keyframes textShimmer": {
                "0%, 100%": {
                  backgroundPosition: "0% 50%",
                },
                "50%": {
                  backgroundPosition: "100% 50%",
                },
              },
              "@keyframes textPulse": {
                "0%, 100%": {
                  transform: "translateX(2px) scale(1)",
                },
                "50%": {
                  transform: "translateX(2px) scale(1.05)",
                },
              },
              "@keyframes textGlow": {
                "0%, 100%": {
                  opacity: 0.2,
                },
                "50%": {
                  opacity: 0.5,
                },
              },
            }}
            title="NOVERA ERP SYSTEM"
          >
            NOVERA
          </Typography>
          <Typography
            variant="caption"
            sx={{ 
              display: "block",
              fontWeight: 700,
              color: alpha(theme.palette.primary.main, 0.9),
              fontSize: "0.6rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontFamily: '"JetBrains Mono", "Fira Code", "Consolas", "SF Mono", monospace',
              mt: 0.2,
              position: "relative",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
              background: `linear-gradient(135deg, 
                ${alpha(theme.palette.primary.main, 0.9)} 0%, 
                ${alpha(theme.palette.secondary.main, 0.8)} 50%,
                ${alpha(theme.palette.primary.main, 0.9)} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              "&::before": {
                content: '""',
                position: "absolute",
                left: "-6px",
                top: "50%",
                transform: "translateY(-50%)",
                width: "4px",
                height: "1.5px",
                background: `linear-gradient(90deg, 
                  ${theme.palette.primary.main} 0%, 
                  ${alpha(theme.palette.secondary.main, 0.8)} 100%)`,
                borderRadius: "1px",
                opacity: 0.6,
                animation: "lineGlow 2.5s ease-in-out infinite",
                boxShadow: `0 0 6px ${alpha(theme.palette.primary.main, 0.3)}`,
              },
              "&::after": {
                content: '""',
                position: "absolute",
                right: "-6px",
                top: "50%",
                transform: "translateY(-50%)",
                width: "3px",
                height: "3px",
                backgroundColor: alpha(theme.palette.primary.main, 0.5),
                borderRadius: "50%",
                animation: "dotPulse 1.8s ease-in-out infinite",
                boxShadow: `0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
              },
              "@keyframes lineGlow": {
                "0%, 100%": {
                  opacity: 0.4,
                  transform: "translateY(-50%) scaleX(0.8)",
                  boxShadow: `0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
                },
                "50%": {
              opacity: 0.8,
                  transform: "translateY(-50%) scaleX(1.2)",
                  boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.4)}`,
                },
              },
              "@keyframes dotPulse": {
                "0%, 100%": {
                  opacity: 0.3,
                  transform: "translateY(-50%) scale(1)",
                  boxShadow: `0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                },
                "50%": {
                  opacity: 0.7,
                  transform: "translateY(-50%) scale(1.3)",
                  boxShadow: `0 0 6px ${alpha(theme.palette.primary.main, 0.3)}`,
                },
              },
            }}
            title={`Licensed for ${workspaceName ?? "Workspace"}`}
          >
            ERP.
          </Typography>
        </Box>
      </Paper>

      {/* Compact Sidebar Toggle - Repositioned */}
      <Box sx={{ 
        position: "absolute", 
        top: 8, 
        right: 8, 
        zIndex: 10 
      }}>
          <IconButton 
            size="small" 
            onClick={handleSidebarToggle}
            sx={{
            width: 28,
            height: 28,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: "blur(8px)",
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
              "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              borderColor: alpha(theme.palette.primary.main, 0.3),
              transform: "scale(1.1)",
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
            },
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            {sidebarOpen ? (
            <ChevronLeftIcon sx={{ fontSize: 16 }} />
            ) : (
            <ChevronRightIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
      </Box>
          
          {/* Mobile close */}
          {isMobile && (
        <IconButton 
          size="small" 
          onClick={handleDrawerToggle}
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            zIndex: 10,
            width: 28,
            height: 28,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: "blur(8px)",
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
            "&:hover": {
              bgcolor: alpha(theme.palette.error.main, 0.1),
              borderColor: alpha(theme.palette.error.main, 0.3),
              transform: "scale(1.1)",
            },
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}

      {/* Professional Profile Section */}
      <Paper
        component={Link}
        to="/profile"
        elevation={2}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          p: 1.2,
          borderRadius: 3,
          mb: 1,
          textDecoration: "none",
          color: "inherit",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          background: theme.palette.mode === "dark"
            ? `linear-gradient(145deg, 
                ${theme.palette.grey[800]} 0%, 
                ${theme.palette.grey[700]} 100%)`
            : `linear-gradient(145deg, 
                ${theme.palette.background.paper} 0%, 
                ${theme.palette.grey[50]} 100%)`,
          border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          position: "relative",
          overflow: "hidden",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: theme.palette.mode === "dark"
              ? `0 8px 25px ${alpha(theme.palette.common.black, 0.4)}, 
                 0 0 0 1px ${alpha(theme.palette.primary.main, 0.3)}`
              : `0 8px 25px ${alpha(theme.palette.common.black, 0.15)}, 
                 0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
            background: theme.palette.mode === "dark"
              ? `linear-gradient(145deg, 
                  ${theme.palette.grey[700]} 0%, 
                  ${theme.palette.grey[600]} 100%)`
              : `linear-gradient(145deg, 
                  ${theme.palette.background.paper} 0%, 
                  ${theme.palette.grey[100]} 100%)`,
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "3px",
            height: "100%",
            background: `linear-gradient(180deg, 
              ${theme.palette.primary.main} 0%, 
              ${alpha(theme.palette.primary.main, 0.6)} 100%)`,
            opacity: 0.8,
          },
        }}
      >
        {/* Professional Avatar Container */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: theme.palette.mode === "dark"
                ? `linear-gradient(135deg, 
                    ${theme.palette.grey[700]} 0%, 
                    ${theme.palette.grey[600]} 100%)`
                : `linear-gradient(135deg, 
                    ${theme.palette.background.paper} 0%, 
                    ${theme.palette.grey[100]} 100%)`,
              border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: theme.palette.mode === "dark"
                ? `0 4px 10px ${alpha(theme.palette.common.black, 0.3)}`
                : `0 4px 10px ${alpha(theme.palette.common.black, 0.1)}`,
              "&:hover": {
                transform: "scale(1.05)",
                borderColor: alpha(theme.palette.primary.main, 0.4),
                boxShadow: theme.palette.mode === "dark"
                  ? `0 6px 15px ${alpha(theme.palette.common.black, 0.4)}, 
                     0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`
                  : `0 6px 15px ${alpha(theme.palette.common.black, 0.15)}, 
                     0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
              },
            }}
          >
            <Avatar
              src={avatarUrl || undefined}
              sx={{
                width: 36,
                height: 36,
                fontSize: 15,
                fontWeight: 700,
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                border: `2px solid ${theme.palette.background.paper}`,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "scale(1.02)",
                },
              }}
            >
              {initials}
            </Avatar>
            
            {/* Enhanced Online Status Indicator Badge */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: 14,
                height: 14,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 3,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, #4CAF50 0%, #66BB6A 50%, #81C784 100%)`,
                  border: `2.5px solid ${theme.palette.background.paper}`,
                  boxShadow: theme.palette.mode === "dark"
                    ? `0 0 0 2px ${alpha("#4CAF50", 0.3)}, 
                       0 0 8px ${alpha("#4CAF50", 0.6)}, 
                       0 0 16px ${alpha("#4CAF50", 0.3)},
                       inset 0 1px 2px ${alpha("#fff", 0.2)}`
                    : `0 0 0 2px ${alpha("#4CAF50", 0.2)}, 
                       0 0 6px ${alpha("#4CAF50", 0.4)}, 
                       0 0 12px ${alpha("#4CAF50", 0.2)},
                       inset 0 1px 2px ${alpha("#fff", 0.3)}`,
                  animation: "badgePulse 2.5s ease-in-out infinite",
                  "@keyframes badgePulse": {
                    "0%, 100%": {
                      opacity: 1,
                      transform: "translate(-50%, -50%) scale(1)",
                      boxShadow: theme.palette.mode === "dark"
                        ? `0 0 0 2px ${alpha("#4CAF50", 0.3)}, 
                           0 0 8px ${alpha("#4CAF50", 0.6)}, 
                           0 0 16px ${alpha("#4CAF50", 0.3)},
                           inset 0 1px 2px ${alpha("#fff", 0.2)}`
                        : `0 0 0 2px ${alpha("#4CAF50", 0.2)}, 
                           0 0 6px ${alpha("#4CAF50", 0.4)}, 
                           0 0 12px ${alpha("#4CAF50", 0.2)},
                           inset 0 1px 2px ${alpha("#fff", 0.3)}`,
                    },
                    "50%": {
                      opacity: 0.9,
                      transform: "translate(-50%, -50%) scale(1.15)",
                      boxShadow: theme.palette.mode === "dark"
                        ? `0 0 0 2px ${alpha("#4CAF50", 0.4)}, 
                           0 0 12px ${alpha("#4CAF50", 0.8)}, 
                           0 0 24px ${alpha("#4CAF50", 0.5)},
                           inset 0 1px 2px ${alpha("#fff", 0.25)}`
                        : `0 0 0 2px ${alpha("#4CAF50", 0.3)}, 
                           0 0 10px ${alpha("#4CAF50", 0.6)}, 
                           0 0 18px ${alpha("#4CAF50", 0.3)},
                           inset 0 1px 2px ${alpha("#fff", 0.35)}`,
                    },
                  },
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "180%",
                  height: "180%",
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${alpha("#4CAF50", 0.4)} 0%, transparent 70%)`,
                  animation: "badgeRipple 2.5s ease-out infinite",
                  pointerEvents: "none",
                  "@keyframes badgeRipple": {
                    "0%": {
                      opacity: 0.6,
                      transform: "translate(-50%, -50%) scale(0.8)",
                    },
                    "50%": {
                      opacity: 0.3,
                      transform: "translate(-50%, -50%) scale(1.2)",
                    },
                    "100%": {
                      opacity: 0,
                      transform: "translate(-50%, -50%) scale(1.6)",
                    },
                  },
                },
              }}
            >
              {/* Inner glow dot */}
              <Box
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, #ffffff 0%, ${alpha("#ffffff", 0.8)} 100%)`,
                  boxShadow: `0 0 4px ${alpha("#ffffff", 0.6)}, inset 0 0 2px ${alpha("#4CAF50", 0.5)}`,
                  animation: "badgeGlow 2s ease-in-out infinite",
                  "@keyframes badgeGlow": {
                    "0%, 100%": {
                      opacity: 0.9,
                      transform: "scale(1)",
                    },
                    "50%": {
                      opacity: 1,
                      transform: "scale(1.2)",
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Professional User Info */}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body1"
            fontWeight={600}
            sx={{ 
              lineHeight: 1.2,
              fontSize: "0.9rem",
              color: theme.palette.text.primary,
              transition: "color 0.3s ease",
              "&:hover": {
                color: theme.palette.primary.main,
              },
            }}
            noWrap
          >
            {fullName}
          </Typography>
          <Typography
            variant="body2"
            sx={{ 
              display: "block", 
              opacity: 0.8,
              fontSize: "0.72rem",
              fontWeight: 500,
              color: theme.palette.text.secondary,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              fontFamily: '"Inter", "Roboto", sans-serif',
              mt: 0.3,
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                left: "-6px",
                top: "50%",
                transform: "translateY(-50%)",
                width: "3px",
                height: "3px",
                backgroundColor: theme.palette.primary.main,
                borderRadius: "50%",
                opacity: 0.6,
              },
            }}
            noWrap
          >
            {role}
          </Typography>
        </Box>
      </Paper>

      <Divider />
    </Box>
  );

  const renderItem = (key: IconMappingKeys) => {
    // HR is special; rendered via renderHR()
    if (key === "HR") return renderHR();
    // CRM is special; rendered via renderCRM()
    if (key === "CRM") return renderCRM();
    // Inventory is special; rendered via renderInventory()
    if (key === "Inventory") return renderInventory();
    // Purchasing is special; rendered via renderPurchasing()
    if (key === "Purchasing") return renderPurchasing();
    // Invoicing is special; rendered via renderInvoicing()
    if (key === "Invoicing") return renderInvoicing();

    const path = pathMap[key as Exclude<IconMappingKeys, "HR" | "CRM" | "Inventory" | "Purchasing" | "Invoicing">] as string;
    const isActive = location.pathname === path;

    return (
      <Fade in timeout={300} key={key}>
        <Tooltip title={key} placement="right" arrow>
          <Box component={Link} to={path} sx={{ textDecoration: "none", color: "inherit" }}>
            <StyledListItemButton
              onClick={() => (isMobile ? handleDrawerToggle() : undefined)}
              selected={isActive}
              dense
            sx={{
              my: 0.5,
              borderRadius: 2.5,
              position: "relative",
              minHeight: 42,
              px: 1.5,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
              },
              ...(isActive && {
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  borderRadius: "0 2px 2px 0",
                  boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.4)}`,
                },
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                },
              }),
            }}
          >
            <ListItemIcon 
              sx={{ 
                minWidth: 40,
                color: isActive ? theme.palette.primary.main : "inherit",
                transition: "all 0.3s ease",
              }}
            >
              {getIconComponent(key)}
            </ListItemIcon>
            <ListItemText
              primary={key}
              primaryTypographyProps={{
                fontSize: "0.875rem",
                fontWeight: isActive ? 700 : 500,
                color: isActive ? theme.palette.primary.main : "inherit",
              }}
              sx={{
                "& .MuiListItemText-primary": {
                  transition: "all 0.3s ease",
                },
              }}
            />
          </StyledListItemButton>
          </Box>
        </Tooltip>
      </Fade>
    );
  };

  const renderHR = () => {
    const parentActive = isHRActive;

    return (
      <Box key="HR">
        <Tooltip title="HR" placement="right" arrow>
        <StyledListItemButton
          onClick={toggleHR}
          selected={parentActive}
          dense
          sx={{
            my: 0.5,
            borderRadius: 2.5,
            position: "relative",
            minHeight: 42,
            px: 1.5,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
            },
            ...(parentActive && {
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 3,
                background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                borderRadius: "0 2px 2px 0",
                boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.15),
              },
            }),
          }}
        >
            <ListItemIcon 
              sx={{ 
                minWidth: 40,
                color: parentActive ? theme.palette.primary.main : "inherit",
                transition: "all 0.3s ease",
              }}
            >
              {getIconComponent("HR")}
            </ListItemIcon>
            <ListItemText
              primary="HR"
              primaryTypographyProps={{
                fontSize: "0.875rem",
                fontWeight: parentActive ? 700 : 500,
                color: parentActive ? theme.palette.primary.main : "inherit",
              }}
              sx={{
                "& .MuiListItemText-primary": {
                  transition: "all 0.3s ease",
                },
              }}
            />
            <Box
              sx={{
                color: parentActive ? theme.palette.primary.main : "inherit",
                transition: "all 0.3s ease",
                transform: hrOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              {hrOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
        </StyledListItemButton>
        </Tooltip>

        <Collapse in={hrOpen} timeout="auto" unmountOnExit>
          <List dense disablePadding>
            {hrChildren.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Tooltip key={item.to} title={item.label} placement="right" arrow>
                  <ListItemButton
                    component={Link}
                    to={item.to}
                    onClick={() => (isMobile ? handleDrawerToggle() : undefined)}
                    dense
                    selected={active}
                    sx={getNestedItemStyles(active)}
                  >
                    <ListItemIcon 
                      sx={{ 
                        minWidth: 36, 
                        color: active ? theme.palette.primary.main : "inherit",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ 
                        fontSize: 13, 
                        noWrap: true,
                        fontWeight: active ? 600 : 500,
                        color: active ? theme.palette.primary.main : "inherit",
                      }}
                      sx={{
                        "& .MuiListItemText-primary": {
                          transition: "all 0.3s ease",
                        },
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
              );
            })}
          </List>
        </Collapse>
      </Box>
    );
  };

  const renderCRM = () => {
    const parentActive = isCRMActive;

    return (
      <Box key="CRM">
        <Tooltip title="CRM" placement="right" arrow>
        <StyledListItemButton
          onClick={toggleCRM}
          selected={parentActive}
          dense
          sx={{
            my: 0.5,
            borderRadius: 2.5,
            position: "relative",
            minHeight: 42,
            px: 1.5,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
            },
            ...(parentActive && {
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 3,
                background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                borderRadius: "0 2px 2px 0",
                boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.15),
              },
            }),
          }}
        >
            <ListItemIcon 
              sx={{ 
                minWidth: 40,
                color: parentActive ? theme.palette.primary.main : "inherit",
                transition: "all 0.3s ease",
              }}
            >
              {getIconComponent("CRM")}
            </ListItemIcon>
            <ListItemText
              primary="CRM"
              primaryTypographyProps={{
                fontSize: "0.875rem",
                fontWeight: parentActive ? 700 : 500,
                color: parentActive ? theme.palette.primary.main : "inherit",
              }}
              sx={{
                "& .MuiListItemText-primary": {
                  transition: "all 0.3s ease",
                },
              }}
            />
            <Box
              sx={{
                color: parentActive ? theme.palette.primary.main : "inherit",
                transition: "all 0.3s ease",
                transform: crmOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              {crmOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
        </StyledListItemButton>
        </Tooltip>

        <Collapse in={crmOpen} timeout="auto" unmountOnExit>
          <List dense disablePadding>
            {crmChildren.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Tooltip key={item.to} title={item.label} placement="right" arrow>
                  <ListItemButton
                    component={Link}
                    to={item.to}
                    onClick={() => (isMobile ? handleDrawerToggle() : undefined)}
                    dense
                    selected={active}
                    sx={getNestedItemStyles(active)}
                  >
                    <ListItemIcon 
                      sx={{ 
                        minWidth: 36, 
                        color: active ? theme.palette.primary.main : "inherit",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ 
                        fontSize: 13, 
                        noWrap: true,
                        fontWeight: active ? 600 : 500,
                        color: active ? theme.palette.primary.main : "inherit",
                      }}
                      sx={{
                        "& .MuiListItemText-primary": {
                          transition: "all 0.3s ease",
                        },
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
              );
            })}
          </List>
        </Collapse>
      </Box>
    );
  };

  const renderInventory = () => {
    const parentActive = isInventoryActive;

    return (
      <Box key="Inventory">
        <Tooltip title="Inventory" placement="right" arrow>
        <StyledListItemButton
          onClick={toggleInventory}
          selected={parentActive}
          dense
          sx={{
            my: 0.5,
            borderRadius: 2.5,
            position: "relative",
            minHeight: 42,
            px: 1.5,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
            },
            ...(parentActive && {
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 3,
                background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                borderRadius: "0 2px 2px 0",
                boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.15),
              },
            }),
          }}
        >
            <ListItemIcon 
              sx={{ 
                minWidth: 40,
                color: parentActive ? theme.palette.primary.main : "inherit",
                transition: "all 0.3s ease",
              }}
            >
              {getIconComponent("Inventory")}
            </ListItemIcon>
            <ListItemText
              primary="Inventory"
              primaryTypographyProps={{
                fontSize: "0.875rem",
                fontWeight: parentActive ? 700 : 500,
                color: parentActive ? theme.palette.primary.main : "inherit",
              }}
              sx={{
                "& .MuiListItemText-primary": {
                  transition: "all 0.3s ease",
                },
              }}
            />
            <Box
              sx={{
                color: parentActive ? theme.palette.primary.main : "inherit",
                transition: "all 0.3s ease",
                transform: inventoryOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              {inventoryOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
        </StyledListItemButton>
        </Tooltip>

        <Collapse in={inventoryOpen} timeout="auto" unmountOnExit>
          <List dense disablePadding>
            {inventoryChildren.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Tooltip key={item.to} title={item.label} placement="right" arrow>
                  <ListItemButton
                    component={Link}
                    to={item.to}
                    onClick={() => (isMobile ? handleDrawerToggle() : undefined)}
                    dense
                    selected={active}
                    sx={getNestedItemStyles(active)}
                  >
                    <ListItemIcon 
                      sx={{ 
                        minWidth: 36, 
                        color: active ? theme.palette.primary.main : "inherit",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ 
                        fontSize: 13, 
                        noWrap: true,
                        fontWeight: active ? 600 : 500,
                        color: active ? theme.palette.primary.main : "inherit",
                      }}
                      sx={{
                        "& .MuiListItemText-primary": {
                          transition: "all 0.3s ease",
                        },
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
              );
            })}
          </List>
        </Collapse>
      </Box>
    );
  };

  const renderPurchasing = () => {
    const parentActive = isPurchasingActive;

    return (
      <Box key="Purchasing">
        <Tooltip title="Purchasing" placement="right" arrow>
        <StyledListItemButton
          onClick={togglePurchasing}
          selected={parentActive}
          dense
          sx={{
            my: 0.5,
            borderRadius: 2.5,
            position: "relative",
            minHeight: 42,
            px: 1.5,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
            },
            ...(parentActive && {
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 3,
                background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                borderRadius: "0 2px 2px 0",
                boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.15),
              },
            }),
          }}
        >
            <ListItemIcon 
              sx={{ 
                minWidth: 40,
                color: parentActive ? theme.palette.primary.main : "inherit",
                transition: "all 0.3s ease",
              }}
            >
              {getIconComponent("Purchasing")}
            </ListItemIcon>
            <ListItemText
              primary="Purchasing"
              primaryTypographyProps={{
                fontSize: "0.875rem",
                fontWeight: parentActive ? 700 : 500,
                color: parentActive ? theme.palette.primary.main : "inherit",
              }}
              sx={{
                "& .MuiListItemText-primary": {
                  transition: "all 0.3s ease",
                },
              }}
            />
            <Box
              sx={{
                color: parentActive ? theme.palette.primary.main : "inherit",
                transition: "all 0.3s ease",
                transform: purchasingOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              {purchasingOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
        </StyledListItemButton>
        </Tooltip>

        <Collapse in={purchasingOpen} timeout="auto" unmountOnExit>
          <List dense disablePadding>
            {purchasingChildren.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Tooltip key={item.to} title={item.label} placement="right" arrow>
                  <ListItemButton
                    component={Link}
                    to={item.to}
                    onClick={() => (isMobile ? handleDrawerToggle() : undefined)}
                    dense
                    selected={active}
                    sx={getNestedItemStyles(active)}
                  >
                    <ListItemIcon 
                      sx={{ 
                        minWidth: 36, 
                        color: active ? theme.palette.primary.main : "inherit",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ 
                        fontSize: 13, 
                        noWrap: true,
                        fontWeight: active ? 600 : 500,
                        color: active ? theme.palette.primary.main : "inherit",
                      }}
                      sx={{
                        "& .MuiListItemText-primary": {
                          transition: "all 0.3s ease",
                        },
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
              );
            })}
          </List>
        </Collapse>
      </Box>
    );
  };

  const renderInvoicing = () => {
    const parentActive = isInvoicingActive;

    return (
      <Box key="Invoicing">
        <Tooltip title="Invoicing" placement="right" arrow>
        <StyledListItemButton
          onClick={toggleInvoicing}
          selected={parentActive}
          dense
          sx={{
            my: 0.5,
            borderRadius: 2.5,
            position: "relative",
            minHeight: 42,
            px: 1.5,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
            },
            ...(parentActive && {
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 3,
                background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                borderRadius: "0 2px 2px 0",
                boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.15),
              },
            }),
          }}
        >
            <ListItemIcon 
              sx={{ 
                minWidth: 40,
                color: parentActive ? theme.palette.primary.main : "inherit",
                transition: "all 0.3s ease",
              }}
            >
              {getIconComponent("Invoicing")}
            </ListItemIcon>
            <ListItemText
              primary="Invoicing"
              primaryTypographyProps={{
                fontSize: "0.875rem",
                fontWeight: parentActive ? 700 : 500,
                color: parentActive ? theme.palette.primary.main : "inherit",
              }}
              sx={{
                "& .MuiListItemText-primary": {
                  transition: "all 0.3s ease",
                },
              }}
            />
            <Box
              sx={{
                color: parentActive ? theme.palette.primary.main : "inherit",
                transition: "all 0.3s ease",
                transform: invoicingOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              {invoicingOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
        </StyledListItemButton>
        </Tooltip>

        <Collapse in={invoicingOpen} timeout="auto" unmountOnExit>
          <List dense disablePadding>
            {invoicingChildren.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Tooltip key={item.to} title={item.label} placement="right" arrow>
                  <ListItemButton
                    component={Link}
                    to={item.to}
                    onClick={() => (isMobile ? handleDrawerToggle() : undefined)}
                    dense
                    selected={active}
                    sx={getNestedItemStyles(active)}
                  >
                    <ListItemIcon 
                      sx={{ 
                        minWidth: 36, 
                        color: active ? theme.palette.primary.main : "inherit",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ 
                        fontSize: 13, 
                        noWrap: true,
                        fontWeight: active ? 600 : 500,
                        color: active ? theme.palette.primary.main : "inherit",
                      }}
                      sx={{
                        "& .MuiListItemText-primary": {
                          transition: "all 0.3s ease",
                        },
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
              );
            })}
          </List>
        </Collapse>
      </Box>
    );
  };

  // Expanded drawer content
  const expandedDrawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {Header}

      <Box sx={{ flex: 1, overflow: "auto", px: 0.5, py: 0.5 }}>
        {grouped.map((group, groupIdx) => (
          <Fade in timeout={400 + groupIdx * 100} key={group.title}>
            <Box sx={{ mb: 1 }}>
              <StyledListSubheader
                sx={{
                  px: 2,
                  py: 1,
                  mt: groupIdx > 0 ? 1.5 : 0,
                }}
              >
                {group.title}
              </StyledListSubheader>
              <List>
                {group.keys.map((k) => renderItem(k))}
              </List>
            </Box>
          </Fade>
        ))}
      </Box>
    </Box>
  );

  // Collapsed drawer content (icons only)
  const collapsedDrawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Box sx={{ width: "100%", py: 1.5, px: 0.5 }}>
        {/* Enhanced Logo */}
        <Fade in timeout={400}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
            <Box
              component="img"
              src={noveraLogo}
              alt="NOVERA ERP SYSTEM"
              sx={{ 
                width: 44, 
                height: 40, 
                borderRadius: 2.5,
                objectFit: "contain",
                p: 0.5,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                },
              }}
            />
          </Box>
        </Fade>
        
        {/* Enhanced Toggle button */}
        <Fade in timeout={500}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            <IconButton 
              size="small" 
              onClick={handleSidebarToggle}
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2.5,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                backdropFilter: "blur(10px)",
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                  borderColor: alpha(theme.palette.primary.main, 0.4),
                  transform: "scale(1.1) rotate(5deg)",
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
                },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
            </IconButton>
          </Box>
        </Fade>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", width: "100%", py: 0.5 }}>
        {grouped.map((group, groupIdx) => (
          <Fade in timeout={600 + groupIdx * 100} key={group.title}>
            <Box>
              {group.keys.map((k) => {
                const path = pathMap[k as Exclude<IconMappingKeys, "HR" | "CRM" | "Inventory" | "Purchasing" | "Invoicing">] as string;
                const isActive = location.pathname === path;
                
                return (
                  <Tooltip key={k} title={k} placement="right" arrow>
                    <Box component={Link} to={path} sx={{ textDecoration: "none", color: "inherit" }}>
                      <ListItemButton
                        onClick={() => (isMobile ? handleDrawerToggle() : undefined)}
                        selected={isActive}
                        dense
                        sx={{
                          minHeight: 52,
                          justifyContent: "center",
                          my: 0.5,
                          mx: 0.5,
                          borderRadius: 2.5,
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          position: "relative",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            transform: "scale(1.05)",
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                          },
                          ...(isActive && {
                            bgcolor: alpha(theme.palette.primary.main, 0.15),
                            border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              left: 0,
                              top: "50%",
                              transform: "translateY(-50%)",
                              width: 3,
                              height: "60%",
                              background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                              borderRadius: "0 2px 2px 0",
                              boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.4)}`,
                            },
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.2),
                            },
                          }),
                        }}
                      >
                        <ListItemIcon 
                          sx={{ 
                            minWidth: "auto", 
                            justifyContent: "center",
                            color: isActive ? theme.palette.primary.main : "inherit",
                            transition: "all 0.3s ease",
                          }}
                        >
                          {getIconComponent(k)}
                        </ListItemIcon>
                      </ListItemButton>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Fade>
        ))}
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? mobileOpen : true}
      onClose={handleDrawerToggle}
      sx={{
        width: sidebarOpen ? drawerWidth : 72,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: sidebarOpen ? drawerWidth : 72,
          boxSizing: "border-box",
          borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          background: theme.palette.mode === "dark"
            ? `linear-gradient(180deg, ${alpha(theme.palette.grey[900], 0.95)} 0%, ${alpha(theme.palette.grey[800], 0.98)} 100%)`
            : `linear-gradient(180deg, ${alpha(theme.palette.grey[50], 0.98)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
          backdropFilter: "saturate(180%) blur(20px)",
          boxShadow: theme.palette.mode === "dark"
            ? `inset -1px 0 0 ${alpha(theme.palette.divider, 0.1)}, 0 0 20px ${alpha(theme.palette.common.black, 0.3)}`
            : `inset -1px 0 0 ${alpha(theme.palette.divider, 0.1)}, 0 0 20px ${alpha(theme.palette.common.black, 0.05)}`,
          position: "relative",
          transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
            pointerEvents: "none",
          },
          },
          ...sx,
      }}
    >
      {sidebarOpen ? expandedDrawer : collapsedDrawer}
    </Drawer>
  );
};

export default SideBar;